import React, { useState } from 'react';
import { Play } from 'lucide-react';
import VideoModal from './landing/VideoModal';
import useReveal, { reveal } from '../hooks/useReveal';
import { VIDEO_STORIES, youTubeThumb } from '../data/site';

const [FEATURED, ...REST] = VIDEO_STORIES;

/*
  Filmed customer stories. One story is given the room to be the headline and
  the rest sit beside it as a list, rather than five identical cards in a
  carousel. Nothing autoplays and nothing rotates on a timer.

  Thumbnails come from YouTube, so no video player is loaded until someone
  actually asks for one (see VideoModal).
*/
const VideoTestimonials = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [ref, isVisible] = useReveal();

  return (
    <section id="video-testimonials" className="ab-section ab-section--tint ab-stories" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-stories__head ${reveal(isVisible)}`}>
          <h2 className="ab-h2">Hear it from the schools</h2>
          <p className="ab-lead">
            Administrators, teachers and parents on what changed after AtadaBoard.
          </p>
        </header>

        <div className="ab-stories__grid">
          <button
            type="button"
            className={`ab-stories__featured ${reveal(isVisible)}`}
            onClick={() => setActiveVideo(FEATURED)}
          >
            <span className="ab-stories__thumb">
              <img
                src={youTubeThumb(FEATURED.videoId)}
                alt=""
                width="1280"
                height="720"
                loading="lazy"
                decoding="async"
              />
              <span className="ab-stories__play">
                <Play size={22} strokeWidth={2} fill="currentColor" />
              </span>
            </span>
            <span className="ab-stories__text">
              <span className="ab-stories__role">{FEATURED.role}</span>
              <span className="ab-stories__title">{FEATURED.headline}</span>
              <span className="ab-stories__summary">{FEATURED.summary}</span>
            </span>
          </button>

          <ul className="ab-stories__list">
            {REST.map((story, index) => (
              <li
                key={story.id}
                className={reveal(isVisible)}
                style={{ '--ab-reveal-delay': `${80 + index * 70}ms` }}
              >
                <button type="button" className="ab-stories__row" onClick={() => setActiveVideo(story)}>
                  <span className="ab-stories__rowThumb">
                    <img
                      src={youTubeThumb(story.videoId)}
                      alt=""
                      width="1280"
                      height="720"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="ab-stories__rowPlay">
                      <Play size={13} strokeWidth={2} fill="currentColor" />
                    </span>
                  </span>
                  <span className="ab-stories__rowText">
                    <span className="ab-stories__role">{story.role}</span>
                    <span className="ab-stories__rowTitle">{story.headline}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {activeVideo && <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />}

      <style jsx>{`
        .ab-stories__head { margin-bottom: 2.5rem; }
        .ab-stories__head .ab-lead { margin-top: 0.875rem; }

        .ab-stories__grid {
          display: grid;
          grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr);
          gap: clamp(1.25rem, 2.5vw, 2rem);
          align-items: stretch;
        }

        .ab-stories__featured {
          display: flex;
          flex-direction: column;
          text-align: left;
          padding: 0;
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          background: var(--ab-paper);
          overflow: hidden;
          cursor: pointer;
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease),
            transform 0.25s var(--ab-ease);
        }

        .ab-stories__featured:hover {
          border-color: var(--ab-line-strong);
          box-shadow: var(--ab-shadow-md);
          transform: translateY(-2px);
        }

        .ab-stories__featured:active { transform: translateY(0); }

        .ab-stories__featured:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
        }

        .ab-stories__thumb {
          position: relative;
          display: block;
          aspect-ratio: 16 / 9;
          background: var(--ab-blue-100);
          overflow: hidden;
        }

        .ab-stories__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s var(--ab-ease);
        }

        .ab-stories__featured:hover .ab-stories__thumb img { transform: scale(1.03); }

        .ab-stories__play {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 62px;
          height: 62px;
          border-radius: var(--ab-r-pill);
          background: var(--ab-paper);
          color: var(--ab-blue);
          box-shadow: var(--ab-shadow-md);
          /* Optical nudge: the triangle's mass sits left of the circle's centre. */
          padding-left: 4px;
          transition: transform 0.25s var(--ab-ease);
        }

        .ab-stories__featured:hover .ab-stories__play {
          transform: translate(-50%, -50%) scale(1.06);
        }

        .ab-stories__text {
          display: block;
          padding: clamp(1.25rem, 2vw, 1.75rem);
        }

        .ab-stories__role {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--ab-blue);
          margin-bottom: 0.375rem;
        }

        .ab-stories__title {
          display: block;
          font-family: var(--ab-font-display);
          font-size: clamp(1.15rem, 1.8vw, 1.375rem);
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1.25;
          color: var(--ab-ink);
        }

        .ab-stories__summary {
          display: block;
          margin-top: 0.5rem;
          font-size: 0.9375rem;
          line-height: 1.55;
          color: var(--ab-ink-2);
          max-width: 52ch;
        }

        .ab-stories__list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 0.75rem;
          /*
            The featured card sets the row height; the list scrolls inside it
            rather than growing the row. height: 0 keeps the list out of the
            row sizing, min-height: 100% then fills the row. The 4px padding
            (cancelled by the margin) stops focus outlines being clipped.
          */
          height: 0;
          min-height: 100%;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-width: thin;
          padding: 4px;
          margin: -4px;
        }

        .ab-stories__list li { display: flex; }
        .ab-stories__list li > button { flex: 1; }

        .ab-stories__row {
          display: flex;
          align-items: center;
          gap: 0.875rem;
          width: 100%;
          padding: 0.6875rem;
          text-align: left;
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-inner);
          background: var(--ab-paper);
          cursor: pointer;
          transition: border-color 0.22s var(--ab-ease), background-color 0.22s var(--ab-ease);
        }

        .ab-stories__row:hover {
          border-color: var(--ab-blue-100);
          background: var(--ab-blue-50);
        }

        .ab-stories__row:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 2px;
        }

        .ab-stories__rowThumb {
          position: relative;
          flex-shrink: 0;
          width: 96px;
          aspect-ratio: 16 / 9;
          border-radius: 8px;
          overflow: hidden;
          background: var(--ab-blue-100);
        }

        .ab-stories__rowThumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .ab-stories__rowPlay {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: var(--ab-r-pill);
          background: rgba(255, 255, 255, 0.94);
          color: var(--ab-blue);
          padding-left: 2px;
        }

        .ab-stories__rowText { min-width: 0; }

        .ab-stories__rowTitle {
          display: block;
          font-family: var(--ab-font-display);
          font-size: 0.9375rem;
          font-weight: 600;
          letter-spacing: -0.015em;
          line-height: 1.35;
          color: var(--ab-ink);
        }

        @media (max-width: 940px) {
          .ab-stories__grid { grid-template-columns: minmax(0, 1fr); }
          /* Stacked, there is no featured card beside the list to size it,
             so cap it at roughly the height the original four rows took. */
          .ab-stories__list {
            height: auto;
            min-height: 0;
            max-height: 22rem;
          }
        }

        @media (max-width: 480px) {
          .ab-stories__rowThumb { width: 76px; }
        }
      `}</style>
    </section>
  );
};

export default VideoTestimonials;
